import {
    TextField,
    MenuItem,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    Select,
    FormHelperText,
    Box,
} from '@mui/material';
import CustomDatePicker from 'components/CustomDatePicker';
import ColorField from './ColorField';


const DynamicField = ({
    field,
    value,
    onChange,
    onBlur,
    error,
    helperText,
    disabled = false,
    resolvedOptions = [],
    setFieldValue,
    touched,
    errors,
}) => {
    const commonProps = {
        label:     field.label,
        name:      field.name,
        onBlur,
        disabled:  disabled || field.readOnly,
        error:     !!error,
        helperText,
        variant:   'filled',
        size:      'small',
        fullWidth:  true,
        sx:        { gridColumn: `span ${field.formSpan || 6}` },
    };

    switch (field.type) {
        case 'textarea':
            return (
                <TextField
                    {...commonProps}
                    value={value ?? ''}
                    onChange={onChange}
                    multiline
                    minRows={3}
                />
            );

        case 'number':
            return (
                <TextField
                    {...commonProps}
                    type="number"
                    value={value ?? ''}
                    onChange={onChange}
                    slotProps={{
                        htmlInput: {
                            ...(field.minValue != null && { min: field.minValue }),
                            ...(field.maxValue != null && { max: field.maxValue }),
                        },
                    }}
                />
            );

        case 'email':
            return (
                <TextField
                    {...commonProps}
                    type="email"
                    value={value ?? ''}
                    onChange={onChange}
                />
            );

        case 'boolean':
            return (
                <Box sx={{ gridColumn: `span ${field.formSpan || 6}`, display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                        label={field.label}
                        control={
                            <Checkbox
                                name={field.name}
                                checked={!!value}
                                onChange={onChange}
                                disabled={disabled || field.readOnly}
                            />
                        }
                    />
                </Box>
            );

        case 'date':
            return (
                <Box sx={{ gridColumn: `span ${field.formSpan || 6}` }}>
                    <CustomDatePicker
                        label={field.label}
                        name={field.name}
                        value={value ?? null}
                        onChange={(name, val) => setFieldValue(name, val)}
                        touched={touched}
                        errors={errors}
                        disabled={disabled || field.readOnly}
                    />
                </Box>
            );

        case 'select': {
            const opts = field.options || [];
            return (
                <TextField
                    {...commonProps}
                    select
                    value={value ?? ''}
                    onChange={onChange}
                >
                    <MenuItem value=""><em>- None -</em></MenuItem>
                    {opts.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                </TextField>
            );
        }

        case 'array': {
            const opts = field.options || resolvedOptions || [];
            const selected = Array.isArray(value) ? value : [];
            return (
                <FormControl
                    variant="filled"
                    size="small"
                    fullWidth
                    error={!!error}
                    disabled={disabled || field.readOnly}
                    sx={{ gridColumn: `span ${field.formSpan || 6}` }}
                >
                    <InputLabel size="small">{field.label}</InputLabel>
                    <Select
                        multiple
                        name={field.name}
                        value={selected}
                        onChange={(e) => setFieldValue(field.name, e.target.value)}
                        onBlur={onBlur}
                        renderValue={(sel) => sel.join(', ')}
                    >
                        {opts.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </Select>
                    {helperText && <FormHelperText>{helperText}</FormHelperText>}
                </FormControl>
            );
        }

        case 'relation': {
            const opts = resolvedOptions || [];
            return (
                <TextField
                    {...commonProps}
                    select
                    value={value ?? ''}
                    onChange={onChange}
                >
                    <MenuItem value=""><em>- None -</em></MenuItem>
                    {opts.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                </TextField>
            );
        }

        case 'color':
            return (
                <ColorField
                    field={field}
                    value={value}
                    setFieldValue={setFieldValue}
                    error={error}
                    helperText={helperText}
                    disabled={disabled}
                />
            );

        // text (default)
        default:
            return (
                <TextField
                    {...commonProps}
                    type="text"
                    value={value ?? ''}
                    onChange={onChange}
                />
            );
    }
};

export default DynamicField;
